/**
 * Paalm · Upsell (15/10/2026) : la fenetre « prix duo » apres l'ajout au panier d'une fiche (blocks/paalm-upsell).
 * 1. Le bloc Achat de la fiche ajoute au panier puis annonce cart:updated (source paalm-fiche) et cart:open.
 *    Si c'est la variante de la fiche, on retient ces deux evenements (le panier ne s'ouvre pas) et on montre l'offre.
 * 2. Oui : le produit propose est ajoute avec la propriete cachee _upsell, puis le panier s'ouvre directement ;
 *    Non ou fermeture : le panier s'ouvre comme d'habitude.
 * 3. Le studio (Acquisition > Upsell) donne l'offre de la fiche (produit propose, textes, couleur), avec le prix
 *    et la remise duo lus en direct dans Shopify ; il recoit vues, acceptations et refus, et peut servir une
 *    version B (test A/B). Pas d'offre ou studio injoignable : pas de fenetre, le panier s'ouvre comme avant.
 * Le prix duo vient du taux de la remise automatique Shopify : jamais de remise inventee.
 */
import { CartUpdatedEvent, ThemeEvents } from '@theme/events';

const euros = (c) => (c / 100).toFixed(2).replace('.', ',') + ' €';
const moins = (t) => '− ' + t;

function visiteuse() {
  try {
    let v = localStorage.getItem('paalmclub');
    if (!v || !/^[a-z0-9]{16}$/.test(v)) {
      v = Array.from(crypto.getRandomValues(new Uint8Array(16)), (x) => 'abcdefghijklmnopqrstuvwxyz0123456789'[x % 36]).join('');
      localStorage.setItem('paalmclub', v);
    }
    return v;
  } catch {
    return null;
  }
}

class PaalmUpsell extends HTMLElement {
  connectedCallback() {
    if (this.pret) return;
    this.pret = true;
    this.c = JSON.parse(this.querySelector('[data-pu-config]').textContent);
    this.studio = (this.dataset.studio || '').replace(/\/$/, '');
    this.visiteur = visiteuse();
    this.appareil = matchMedia('(max-width: 749px)').matches ? 'mobile' : 'ordinateur';
    this.carte = this.querySelector('.pu-carte');
    this.voile = this.querySelector('.pu-voile');
    this.version = 'A';
    this.test = null;
    this.vue = null;
    this.arme = 0;
    this.retenu = null;
    this.bloque = false;
    this.montree = false;

    // L'offre de la fiche et la version B eventuelle (test A/B). Sans reponse en 2,5 s : pas de fenetre.
    this.config = this.charger().catch(() => null);

    // L'ajout vient-il de la variante de la fiche ? (clic sur le bouton du bloc Achat ou sur la barre collante)
    document.addEventListener('click', (e) => {
      const b = e.target.closest('[data-pf-ajouter], [data-pf-ajouter-collant]');
      if (!b) return;
      const cta = document.querySelector('[data-pf-ajouter]');
      if (!cta || this.montree || this.sansOffre) return;
      // L'offre n'est peut-etre pas encore arrivee : la variante est verifiee juste avant d'afficher la fenetre.
      this.arme = Date.now();
      this.varianteAjoutee = Number(cta.dataset.variante);
    }, true);
    window.addEventListener(ThemeEvents.cartUpdated, (e) => this.surMiseAJour(e), true);
    window.addEventListener(ThemeEvents.cartOpen, (e) => { if (this.bloque) e.stopImmediatePropagation(); }, true);

    this.querySelectorAll('[data-pu-fermer]').forEach((b) => b.addEventListener('click', () => this.fermer(b.hasAttribute('data-pu-refus') || !this.carte.classList.contains('accepte'))));
    this.querySelector('[data-pu-oui]').addEventListener('click', () => this.accepter());
    this.querySelector('[data-pu-panier]').addEventListener('click', () => this.fermer(false, true));
    this.carte.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.fermer(!this.carte.classList.contains('accepte')); });
  }

  async charger() {
    if (!this.studio) return null;
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 2500);
    try {
      // GET sans en-tete : requete simple (pas de pre-verification), reponse mise en cache par l'hebergeur.
      const r = await fetch(this.studio + '/api/upsell?fiche=' + encodeURIComponent(this.c.fiche), { signal: ctrl.signal });
      const d = await r.json();
      const o = d && d.offre;
      if (!o || !o.produit) {
        this.sansOffre = true;
        return null;
      }
      this.c.ficheVariante = o.ficheVariante;
      this.c.fichePrix = o.fichePrix;
      this.c.senteur = o.senteur;
      this.c.macaron = o.macaron;
      this.c.remise = Number(o.produit.remise || 0) * 100;
      this.style.setProperty('--pu-senteur', o.couleur);
      this.c.a = { produit: o.produit, surtitre: o.surtitre, titre: o.titre, titreSurligne: o.titreSurligne, raison: o.raison, bouton: o.bouton, refus: o.refus };
      this.vue = this.c.a;
      const test = d.test;
      if (!test || !test.id || !test.b) return o;
      let v = test.fixe === 'A' || test.fixe === 'B' ? test.fixe : null;
      if (!v) {
        const cle = 'pu-test-' + test.id;
        try { v = localStorage.getItem(cle); } catch {}
        if (v !== 'A' && v !== 'B') {
          v = Math.random() < 0.5 ? 'A' : 'B';
          try { localStorage.setItem(cle, v); } catch {}
        }
      }
      this.test = test.id;
      this.version = v;
      if (v === 'B') this.vue = Object.assign({}, this.c.a, test.b, { produit: test.b.produit ? Object.assign({ dispo: true }, test.b.produit) : this.c.a.produit });
      if (v === 'B' && test.b.macaron) this.c.macaron = test.b.macaron;
      if (v === 'B' && test.b.produit) this.remiseB = Number(test.b.produit.remise || 0) * 100;
      return o;
    } finally {
      clearTimeout(t);
    }
  }

  surMiseAJour(e) {
    const d = e.detail && e.detail.data;
    if (!this.arme || Date.now() - this.arme > 8000 || !d || d.source !== 'paalm-fiche') return;
    this.arme = 0;
    // On garde la mise a jour du panier : le panier s'ouvrira apres le choix de la cliente.
    e.stopImmediatePropagation();
    this.retenu = { resource: e.detail.resource, data: Object.assign({}, d) };
    this.bloque = true;
    this.preparer().catch(() => this.liberer(true));
  }

  async preparer() {
    const offre = await this.config;
    // Pas d'offre pour cette fiche, ou ce n'est pas la variante qui la declenche : le panier s'ouvre comme avant.
    if (!offre || !this.vue || Number(this.varianteAjoutee) !== Number(this.c.ficheVariante)) return this.liberer(true);
    const p = this.vue.produit;
    // Deja dans le panier, ou indisponible : pas d'offre.
    const panier = await fetch((window.Theme && Theme.routes && Theme.routes.cart_url ? Theme.routes.cart_url : '/cart') + '.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    if (!p || p.dispo === false || panier.items.some((i) => Number(i.variant_id) === Number(p.variantId) || i.handle === p.handle)) return this.liberer(true);
    this.remplir();
    this.ouvrir();
  }

  prix() {
    const p = this.vue.produit;
    const taux = this.version === 'B' && this.remiseB !== undefined ? this.remiseB : Number(this.c.remise) || 0;
    // Shopify arrondit la remise au centime INFERIEUR, ligne par ligne (constate le 15/10/2026 : 24,90 € et 16,90 €
    // a 9,99 % donnent -2,48 € et -1,68 €, soit 37,64 € au total).
    const rf = Math.floor((this.c.fichePrix * taux) / 100 + 1e-9);
    const rp = Math.floor((p.prix * taux) / 100 + 1e-9);
    const eco = taux > 0 ? rf + rp : 0;
    return { normal: p.prix, duo: p.prix - eco, eco, pct: p.prix ? Math.floor((eco / p.prix) * 100) : 0 };
  }

  remplir() {
    const v = this.vue, p = v.produit, x = this.prix();
    const ecrire = (s, t) => { const n = this.querySelector(s); if (n) n.textContent = t; };
    const img = this.querySelector('[data-pu-image]');
    img.src = p.image || '';
    img.alt = p.titre;
    ecrire('[data-pu-surtitre]', (v.surtitre || '').replace('[senteur]', this.c.senteur || ''));
    ecrire('[data-pu-titre]', v.titre || '');
    ecrire('[data-pu-surligne]', v.titreSurligne || '');
    ecrire('[data-pu-prix]', euros(x.duo));
    ecrire('[data-pu-barre]', x.eco ? euros(x.normal) : '');
    ecrire('[data-pu-eco]', x.eco ? moins(euros(x.eco)) : '');
    ecrire('[data-pu-raison]', x.eco ? v.raison || '' : '');
    ecrire('[data-pu-bouton]', v.bouton || 'Je veux en profiter');
    ecrire('[data-pu-refus]', v.refus || 'Non merci');
    const mac = this.querySelector('[data-pu-macaron]');
    mac.hidden = !x.eco;
    mac.querySelector('b').textContent = this.c.macaron === 'euros' ? moins(euros(x.eco)) : moins(x.pct + ' %');
    this.querySelector('[data-pu-fait1]').src = this.c.ficheImage || '';
    this.querySelector('[data-pu-fait2]').src = p.image || '';
    ecrire('[data-pu-fait-titre]', 'Ton duo ' + (this.c.senteur || '') + ' est prêt');
    const cible = /^brume/i.test(p.titre) ? 'ta brume' : /d[ée]o/i.test(p.titre) ? 'ton déo' : p.titre;
    ecrire('[data-pu-fait-texte]', x.eco ? euros(x.eco) + ' économisés sur ' + cible : 'Ajouté à ton panier');
  }

  ouvrir() {
    this.montree = true;
    this.voile.hidden = false;
    this.carte.hidden = false;
    requestAnimationFrame(() => this.classList.add('ouvert'));
    document.documentElement.style.overflow = 'hidden';
    setTimeout(() => this.querySelector('[data-pu-oui]').focus({ preventScroll: true }), 50);
    this.envoyer('vue');
  }

  async accepter() {
    const p = this.vue.produit, b = this.querySelector('[data-pu-oui]'), err = this.querySelector('[data-pu-erreur]');
    const tiroir = document.querySelector('cart-drawer');
    b.classList.add('charge'); err.hidden = true;
    try {
      const r = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(p.variantId), quantity: 1, properties: { _upsell: [this.c.fiche, p.handle, this.test || '-', this.version].join('|') } }], sections: tiroir && tiroir.dataset.sectionId ? tiroir.dataset.sectionId : '' }),
      });
      const d = await r.json();
      if (!r.ok || d.status) throw new Error(d.description || d.message || '');
      this.envoyer('accepte');
      const compte = this.retenu && this.retenu.data.itemCount != null ? this.retenu.data.itemCount + 1 : undefined;
      this.retenu = { resource: {}, data: Object.assign({}, this.retenu ? this.retenu.data : {}, { itemCount: compte, sections: d.sections, source: 'paalm-upsell' }) };
      // Oui : la fenetre se ferme et le panier s'ouvre aussitot avec le produit ajoute (pas d'ecran intermediaire,
      // demande de Kevin du 15/10/2026 : une etape de moins).
      this.fermer(false, true);
    } catch (e) {
      err.textContent = e.message || 'Ajout impossible, réessaie dans un instant.';
      err.hidden = false;
    } finally {
      b.classList.remove('charge');
    }
  }

  /** refus : la cliente a dit non (ou ferme) ; ouvrirPanier : ouvrir le panier apres. */
  fermer(refus, ouvrirPanier) {
    if (!this.classList.contains('ouvert')) return;
    if (refus) this.envoyer('refuse');
    this.classList.remove('ouvert');
    document.documentElement.style.overflow = '';
    setTimeout(() => { this.voile.hidden = true; this.carte.hidden = true; }, 320);
    // Apres un refus, le panier s'ouvre comme d'habitude ; apres « Continuer mes achats », il se met a jour sans s'ouvrir.
    this.liberer(refus || Boolean(ouvrirPanier));
  }

  liberer(ouvrirPanier) {
    this.bloque = false;
    if (!this.retenu) return;
    const { resource, data } = this.retenu;
    this.retenu = null;
    const icone = document.querySelector('cart-icon');
    const avant = icone ? icone.addToCartBehavior : null;
    if (icone && !ouvrirPanier) icone.addToCartBehavior = 'aucun';
    document.dispatchEvent(new CartUpdatedEvent(resource, data));
    if (icone && !ouvrirPanier) icone.addToCartBehavior = avant;
    if (ouvrirPanier) document.dispatchEvent(new CustomEvent(ThemeEvents.cartOpen, { bubbles: true }));
  }

  envoyer(type) {
    if (!this.studio || !this.visiteur) return;
    const p = this.vue.produit;
    const corps = JSON.stringify({ action: 'evenement', type, visiteur: this.visiteur, fiche: this.c.fiche, ficheNom: this.c.ficheNom, propose: p.handle, proposeNom: p.titre, test: this.test, version: this.test ? this.version : null, appareil: this.appareil });
    try {
      if (!navigator.sendBeacon || !navigator.sendBeacon(this.studio + '/api/upsell', new Blob([corps], { type: 'text/plain' })))
        fetch(this.studio + '/api/upsell', { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: corps, keepalive: true }).catch(() => {});
    } catch {}
  }
}

if (!customElements.get('paalm-upsell')) customElements.define('paalm-upsell', PaalmUpsell);
